/**
 * VP-329 — jedno miejsce, w którym front pyta „czy pokazywać Stripe'a".
 *
 * Shopify zawiesił nasz submission za wymóg App Store §1.2.1: recenzent wszedł
 * z Ustawień na `checkout.stripe.com`. Merchant, który zainstalował apkę ze sklepu,
 * ma płacić przez Shopify App Pricing i NIE MOŻE zobaczyć naszego checkoutu,
 * portalu, token packów ani trialu.
 *
 * Zasada: żadnych rozsianych `if (shopify)` po komponentach — każdy warunek
 * przechodzi przez ten hook, żeby przy kolejnej zmianie nie zostało zapomniane CTA.
 * Backend i tak trzyma bezpiecznik (`requireStripeBillingProvider` → 409).
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  normalizeBillingProvider,
  usesShopifyBilling,
  type BillingProvider,
} from "@shared/billing-provider";

export type ShopifyBillingStatus = {
  provider: BillingProvider;
  planKey: string | null;
  planHandle: string | null;
  subscriptionStatus: string | null;
  /** Hostowana przez Shopify strona planów — jedyne miejsce zmiany planu. */
  manageUrl: string | null;
};

export function useBillingProvider(): {
  provider: BillingProvider;
  /** Czy wolno pokazać cokolwiek ze Stripe'a (plany, checkout, portal, token packi). */
  showsStripeBilling: boolean;
  isShopifyBilling: boolean;
} {
  const { user } = useAuth();
  const provider = normalizeBillingProvider(user?.billingProvider);
  return {
    provider,
    // Konta `manual` (demo, umowy indywidualne) też nie widzą siatki planów — to ta
    // ścieżka (`subscription_active` bez klienta Stripe) wpuściła recenzenta na checkout.
    showsStripeBilling: provider === "stripe",
    isShopifyBilling: usesShopifyBilling(provider),
  };
}

/** Szczegóły planu Shopify (handle, status, link do zmiany planu) — tylko dla kont Shopify. */
export function useShopifyBillingStatus() {
  const { isShopifyBilling } = useBillingProvider();
  return useQuery<ShopifyBillingStatus>({
    queryKey: ["/api/integrations/shopify/billing/status"],
    enabled: isShopifyBilling,
    staleTime: 60_000,
  });
}
